import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getIntelligenceReport,
  listIntelligenceReports,
  requestIntelligenceReport,
} from '../../api/endpoints/intelligence';
import type { IntelligenceReportSummary } from '../../api/intelligenceReportSchemas';

/** A report older than this is stale, so opening Inteligencia asks for a fresh one. */
export const REPORT_STALE_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const INTELLIGENCE_REPORTS_KEY = ['intelligence-reports'] as const;
export const intelligenceReportKey = (id: string) => ['intelligence-report', id] as const;

/** True when there is no report yet or the newest one is older than `REPORT_STALE_DAYS`. */
export function isReportStale(
  latest: Pick<IntelligenceReportSummary, 'createdAt'> | undefined,
  now: Date = new Date(),
): boolean {
  if (!latest) return true;
  const createdAt = Date.parse(latest.createdAt);
  if (Number.isNaN(createdAt)) return true;
  return now.getTime() - createdAt > REPORT_STALE_DAYS * MS_PER_DAY;
}

export function useIntelligenceReports(enabled = true) {
  return useQuery({ queryKey: INTELLIGENCE_REPORTS_KEY, queryFn: listIntelligenceReports, enabled });
}

export function useIntelligenceReport(id: string | undefined) {
  return useQuery({
    queryKey: intelligenceReportKey(id ?? ''),
    queryFn: () => getIntelligenceReport(id ?? ''),
    enabled: Boolean(id),
  });
}

/** Seeds the detail cache with the new report and refreshes the list behind it. */
export function useRequestIntelligenceReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: requestIntelligenceReport,
    onSuccess: ({ data }) => {
      queryClient.setQueryData(intelligenceReportKey(data.id), data);
      void queryClient.invalidateQueries({ queryKey: INTELLIGENCE_REPORTS_KEY });
    },
  });
}
